import React, { useEffect, useState, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ChevronLeftIcon,
  DocumentTextIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ShieldCheckIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { stripTtrSeedDemoSuffix } from '../../utils/ttrMemberDisplay'
import HelpScreenButton from '../../components/help/HelpScreenButton'
import ScreenHelpPanel from '../../components/help/ScreenHelpPanel'

const TTR_DETAIL_HELP_SCREEN_KEY = 'page:compliance-safe/ttr/detail'

// ─── Shared presentation helpers ─────────────────────────────────────────

function Badge({ bg, color, border, children, title }) {
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: bg, color, border: border ? `1px solid ${border}` : undefined }}
    >
      {children}
    </span>
  )
}

const STATUS_CONFIG = {
  draft:                { label: 'Draft',        bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  ready_for_submission: { label: 'Ready',        bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  filed:                { label: 'Filed',        bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  not_required:         { label: 'Not required', bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
  cancelled:            { label: 'Cancelled',    bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' }
  return <Badge bg={cfg.bg} color={cfg.color} border={cfg.border}>{cfg.label}</Badge>
}

function formatAud(v) {
  if (v == null) return '—'
  const n = Number(v)
  return Number.isFinite(n)
    ? n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 2 })
    : '—'
}

function formatDateTime(v) {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })
}

function DeadlinePill({ deadlineAt, status }) {
  if (!deadlineAt || ['filed', 'not_required', 'cancelled'].includes(status)) {
    return <span className="text-sm text-gray-400">—</span>
  }
  const diffMs = new Date(deadlineAt).getTime() - Date.now()
  if (Number.isNaN(diffMs)) return <span className="text-sm text-gray-400">—</span>
  const overdue = diffMs < 0
  const hours = Math.abs(diffMs) / 3_600_000
  const days = Math.floor(hours / 24)
  const hoursRem = Math.floor(hours - days * 24)
  const label = overdue ? `Overdue by ${days}d ${hoursRem}h` : `${days}d ${hoursRem}h remaining`
  const color = overdue ? '#991b1b' : hours < 24 ? '#991b1b' : hours < 48 ? '#92400e' : '#166534'
  const bg = overdue ? '#fee2e2' : hours < 24 ? '#fee2e2' : hours < 48 ? '#fef3c7' : '#dcfce7'
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-sm font-mono font-medium"
      style={{ backgroundColor: bg, color }}
    >
      <ClockIcon className="w-3.5 h-3.5" />
      {label}
    </span>
  )
}

function DataRow({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm text-gray-800 text-right ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</span>
    </div>
  )
}

// ─── AUSTRAC filing modal ────────────────────────────────────────────────

function toLocalDatetimeInput(d) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function FileTtrModal({ open, onClose, onSubmit, submitting }) {
  const [method, setMethod] = useState('manual')
  const [reference, setReference] = useState('')
  const [filedAtLocal, setFiledAtLocal] = useState(() => {
    const d = new Date(); d.setSeconds(0, 0); return toLocalDatetimeInput(d)
  })
  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (open) {
      setMethod('manual')
      setReference('')
      const d = new Date(); d.setSeconds(0, 0)
      setFiledAtLocal(toLocalDatetimeInput(d))
      setNotes('')
    }
  }, [open])

  if (!open) return null
  const ok = reference.trim() && filedAtLocal && method

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} role="presentation" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg border border-gray-100">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-psg-teal">Mark TTR as filed with AUSTRAC</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-600 leading-relaxed">
            Record the AUSTRAC lodgement details once the TTR has been submitted via AUSTRAC Online.
            Use <b>Manual</b> for a form completed directly on the AUSTRAC portal; <b>Portal</b> for an integrated channel; <b>System</b> is reserved for future in-app submission.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="ttr-file-method" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Filing method <span className="text-red-500">*</span>
              </label>
              <select
                id="ttr-file-method"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white outline-none focus:border-gray-300"
              >
                <option value="manual">Manual (AUSTRAC Online)</option>
                <option value="portal">Portal (integrated)</option>
                <option value="system">System</option>
              </select>
            </div>
            <div>
              <label htmlFor="ttr-file-at" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Filed at <span className="text-red-500">*</span>
              </label>
              <input
                id="ttr-file-at"
                type="datetime-local"
                value={filedAtLocal}
                onChange={(e) => setFiledAtLocal(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-gray-300"
              />
            </div>
          </div>
          <div>
            <label htmlFor="ttr-file-ref" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              AUSTRAC reference <span className="text-red-500">*</span>
            </label>
            <input
              id="ttr-file-ref"
              type="text"
              maxLength={64}
              placeholder="e.g. TTR-2026-00123 (from AUSTRAC Online)"
              value={reference}
              onChange={(e) => setReference(e.target.value.slice(0, 64))}
              className={`w-full px-3 py-2 rounded-lg border text-sm font-mono outline-none focus:border-gray-300 ${
                reference.trim() ? 'border-green-300' : 'border-gray-200'
              }`}
            />
          </div>
          <div>
            <label htmlFor="ttr-file-notes" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Filing notes (optional)
            </label>
            <textarea
              id="ttr-file-notes"
              rows={2}
              maxLength={4000}
              placeholder="Lodgement confirmation, channel details, etc."
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 4000))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 resize-y outline-none focus:border-gray-300"
            />
          </div>
          <div className="text-xs text-gray-500 leading-relaxed border-t border-gray-100 pt-2">
            Filing is final (§17.3). Once recorded, the TTR becomes immutable; corrections must go through the AUSTRAC amendment process.
          </div>
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 rounded-b-xl">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            type="button"
            size="sm"
            className="bg-psg-teal hover:bg-psg-teal-700"
            disabled={!ok || submitting}
            onClick={() => onSubmit({
              austrac_reference: reference.trim(),
              filing_method: method,
              filed_at: filedAtLocal ? new Date(filedAtLocal).toISOString() : new Date().toISOString(),
              notes: notes.trim(),
            })}
          >
            <CheckCircleIcon className="w-4 h-4 mr-1.5" />
            {submitting ? 'Filing…' : 'Mark as Filed'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Mark-Not-Required modal ─────────────────────────────────────────────

function MarkNotRequiredModal({ open, onClose, onSubmit, submitting }) {
  const [reason, setReason] = useState('')
  useEffect(() => { if (open) setReason('') }, [open])
  if (!open) return null
  const ok = reason.trim().length >= 10
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} role="presentation" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg border border-gray-100">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-psg-teal">Mark TTR as not required</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-600 leading-relaxed">
            Use this when the TTR obligation has already been met via a different AUSTRAC report
            (e.g. an SMR covering the same transaction). The reason becomes part of the permanent
            audit record and cannot be edited after submission.
          </p>
          <label htmlFor="ttr-nr-reason" className="block text-sm font-semibold text-gray-600">
            Reason (minimum 10 characters)
          </label>
          <textarea
            id="ttr-nr-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={5}
            placeholder="Describe why this TTR is not required. Reference the alternative report id if applicable."
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 resize-y outline-none focus:border-gray-300"
            autoFocus
          />
          <div className="text-xs text-gray-500">{reason.length} / 10 chars minimum</div>
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 rounded-b-xl">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            type="button"
            size="sm"
            className="bg-psg-teal hover:bg-psg-teal-700"
            disabled={!ok || submitting}
            onClick={() => onSubmit(reason.trim())}
          >
            {submitting ? 'Marking…' : 'Mark not required'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Main component ──────────────────────────────────────────────────────

export default function TtrDetail() {
  const { id } = useParams()
  const [ttr, setTtr] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [markingReady, setMarkingReady] = useState(false)
  const [notRequiredOpen, setNotRequiredOpen] = useState(false)
  const [markingNotRequired, setMarkingNotRequired] = useState(false)
  const [filingOpen, setFilingOpen] = useState(false)
  const [filing, setFiling] = useState(false)
  const [helpPanelOpen, setHelpPanelOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/ttr-registry/${encodeURIComponent(id)}`))
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setTtr(json.data)
    } catch (e) {
      setError(e.message || 'Failed to load TTR')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  const handleMarkReady = useCallback(async () => {
    if (markingReady) return
    setActionError(null)
    setMarkingReady(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/ttr-registry/${encodeURIComponent(id)}/mark-ready`),
        { method: 'POST' }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) {
        const suffix = Array.isArray(json.missing) && json.missing.length
          ? ` Missing: ${json.missing.join(', ')}`
          : ''
        throw new Error((json.error || `HTTP ${res.status}`) + suffix)
      }
      await load()
    } catch (e) {
      setActionError(e.message || 'Failed to mark ready')
    } finally {
      setMarkingReady(false)
    }
  }, [id, markingReady, load])

  const handleMarkNotRequired = useCallback(async (reason) => {
    if (markingNotRequired) return
    setActionError(null)
    setMarkingNotRequired(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/ttr-registry/${encodeURIComponent(id)}/mark-not-required`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason }),
        }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setNotRequiredOpen(false)
      await load()
    } catch (e) {
      setActionError(e.message || 'Failed to mark not required')
    } finally {
      setMarkingNotRequired(false)
    }
  }, [id, markingNotRequired, load])

  const handleFile = useCallback(async ({ austrac_reference, filing_method, filed_at, notes }) => {
    if (filing) return
    setActionError(null)
    setFiling(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/ttr-registry/${encodeURIComponent(id)}/status`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status: 'filed',
            filing: {
              austrac_reference,
              filing_method,
              filed_at,
              notes: notes || undefined,
            },
          }),
        }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setFilingOpen(false)
      await load()
    } catch (e) {
      setActionError(e.message || 'Failed to mark filed')
    } finally {
      setFiling(false)
    }
  }, [id, filing, load])

  if (loading) {
    return (
      <div className="space-y-6">
        <Link to="/compliance-safe/ttr-registry" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-600">
          <ChevronLeftIcon className="w-4 h-4" /> Back to TTR Registry
        </Link>
        <div className="py-12 text-center text-gray-500">Loading…</div>
      </div>
    )
  }

  if (error || !ttr) {
    return (
      <div className="space-y-6">
        <Link to="/compliance-safe/ttr-registry" className="inline-flex items-center gap-2 text-sm font-medium text-psg-teal hover:underline">
          <ChevronLeftIcon className="w-4 h-4" /> Back to TTR Registry
        </Link>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center">
            <ExclamationTriangleIcon className="mx-auto mb-2 w-8 h-8 text-red-500" />
            <h2 className="text-lg font-semibold text-red-700">TTR record not found</h2>
            <p className="text-sm text-red-600 mt-1">{error || `No record found with id: ${id}`}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const formData = ttr.form_data || {}
  const tx = formData.transaction || {}
  const customer = formData.customer || {}
  const missing = Array.isArray(ttr.missing_fields) ? ttr.missing_fields : []

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <Link to="/compliance-safe/ttr-registry" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-600">
          <ChevronLeftIcon className="w-4 h-4" /> Back to TTR Registry
        </Link>
        <HelpScreenButton
          screenKey={TTR_DETAIL_HELP_SCREEN_KEY}
          onClick={() => setHelpPanelOpen(true)}
        />
      </div>

      {/* Header */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <DocumentTextIcon className="w-5 h-5 text-psg-teal" />
                <h2 className="text-xl font-bold text-psg-teal m-0">{ttr.id}</h2>
                <StatusBadge status={ttr.status} />
                <Badge
                  bg={ttr.regime_version === '2006' ? '#f3e8ff' : '#e0f2fe'}
                  color={ttr.regime_version === '2006' ? '#6b21a8' : '#075985'}
                  title={ttr.form_variant === 'ttr_gs_reformed' ? 'Reformed TTR-GS form (from 1 Jul 2026)' : 'Current TTR-GS form'}
                >
                  Regime {ttr.regime_version}
                </Badge>
                <Badge bg="#f1f5f9" color="#475569">
                  {ttr.form_variant === 'ttr_gs_reformed' ? 'Reformed form' : 'Current form'}
                </Badge>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Threshold transaction report generated from payout <span className="font-mono">{ttr.payout_id}</span>.
                Cash <b>{formatAud(ttr.cash_amount)}</b> met the threshold <b>{formatAud(ttr.threshold_applied)}</b> applicable at the transaction date.
              </p>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-1">
              <span className="text-xs text-gray-500 uppercase tracking-wide">AUSTRAC Deadline</span>
              <DeadlinePill deadlineAt={ttr.deadline_at} status={ttr.status} />
              <span className="text-xs text-gray-400 font-mono">{formatDateTime(ttr.deadline_at)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two-column body: left = transaction + customer; right = status summary + missing fields */}
      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-bold text-psg-teal mb-2">Transaction (Section C)</div>
              <DataRow label="Transaction date"  value={tx.date || formatDateTime(ttr.transaction_timestamp).split(',')[0]} />
              <DataRow label="Transaction time"  value={tx.time || ''} />
              <DataRow label="Total payout"      value={formatAud(tx.amount_aud)} mono />
              <DataRow label="Cash portion"      value={formatAud(ttr.cash_amount)} mono />
              <DataRow label="Threshold applied" value={formatAud(ttr.threshold_applied)} mono />
              <DataRow label="Payment method"    value={tx.payment_method} />
              <DataRow label="EGM id"            value={tx.egm_id} mono />
              <DataRow label="TITO ticket"       value={tx.ticket_barcode} mono />
              <DataRow label="Receipt ref"       value={tx.receipt_ref} mono />
              <DataRow label="Session id"        value={tx.session_id} mono />
              <DataRow label="Payout id"         value={ttr.payout_id} mono />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-bold text-psg-teal mb-2">Customer (Section D)</div>
              <DataRow label="Member id"         value={customer.member_id} mono />
              <DataRow label="Member name"       value={stripTtrSeedDemoSuffix(customer.member_name) ?? customer.member_name} />
              <DataRow label="Master profile id" value={customer.master_profile_id} mono />
              <DataRow label="Occupation"        value={customer.occupation} />
              <DataRow label="KYC status"        value={customer.kyc_status} />
              <DataRow label="AML risk"          value={customer.aml_risk} />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-3">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-bold text-psg-teal mb-2">Status</div>
              <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                <span className="text-sm text-gray-500">Current</span>
                <StatusBadge status={ttr.status} />
              </div>
              <DataRow label="Created"         value={formatDateTime(ttr.created_at)} />
              <DataRow label="Last updated"    value={formatDateTime(ttr.updated_at)} />
              <DataRow label="AUSTRAC ref"     value={ttr.austrac_reference} mono />
              <DataRow label="Filed at"        value={formatDateTime(ttr.austrac_filed_at)} />
              <DataRow label="Filing method"   value={ttr.austrac_filing_method} />
              <DataRow label="Filed by"        value={ttr.filed_by_name || ttr.filed_by} />
              {ttr.not_required_reason && (
                <DataRow label="Not required reason" value={ttr.not_required_reason} />
              )}
            </CardContent>
          </Card>

          <Card className={missing.length > 0 ? 'border-amber-200' : ''}>
            <CardContent className="p-4">
              <div className="text-sm font-bold text-psg-teal mb-2 flex items-center gap-2">
                Missing fields
                {missing.length > 0 && (
                  <Badge bg="#fef3c7" color="#92400e">{missing.length}</Badge>
                )}
              </div>
              {missing.length === 0 ? (
                <p className="text-sm text-gray-500">All required fields populated.</p>
              ) : (
                <ul className="space-y-1">
                  {missing.map((f) => (
                    <li key={f} className="text-sm text-gray-700 flex items-start gap-2">
                      <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                      <span className="font-mono text-xs">{f}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* CO action bar — locked once the TTR hits a terminal status. */}
          {!['filed', 'not_required', 'cancelled'].includes(ttr.status) && (
            <Card>
              <CardContent className="p-4">
                <div className="text-sm font-bold text-psg-teal mb-2">Actions</div>
                <div className="space-y-2">
                  {ttr.status === 'draft' && (
                    <Button
                      type="button"
                      className="w-full bg-psg-teal hover:bg-psg-teal-700"
                      size="sm"
                      onClick={handleMarkReady}
                      disabled={markingReady || missing.length > 0}
                      title={missing.length > 0 ? 'Resolve missing fields first' : 'Move this TTR to Ready for Submission'}
                    >
                      <ShieldCheckIcon className="w-4 h-4 mr-1.5" />
                      {markingReady ? 'Marking…' : 'Mark Ready for Submission'}
                    </Button>
                  )}
                  {ttr.status === 'ready_for_submission' && (
                    <Button
                      type="button"
                      className="w-full bg-psg-teal hover:bg-psg-teal-700"
                      size="sm"
                      onClick={() => setFilingOpen(true)}
                      disabled={filing}
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-1.5" />
                      Mark as Filed
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full text-red-700 border-red-200 hover:bg-red-50"
                    onClick={() => setNotRequiredOpen(true)}
                    disabled={markingNotRequired}
                  >
                    <NoSymbolIcon className="w-4 h-4 mr-1.5" />
                    Mark not required
                  </Button>
                </div>
                {actionError && (
                  <div className="mt-3 p-2.5 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
                    <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{actionError}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Activity log — the audit trail surface. */}
      <Card>
        <CardContent className="p-4">
          <div className="text-sm font-bold text-psg-teal mb-3">Activity log</div>
          {Array.isArray(ttr.activity_log) && ttr.activity_log.length > 0 ? (
            <ul className="space-y-3">
              {ttr.activity_log.slice().reverse().map((entry, i) => (
                <li key={`${entry.ts || i}-${i}`} className="border-l-2 border-psg-teal/30 pl-3">
                  <div className="text-xs font-mono text-gray-500">
                    {formatDateTime(entry.ts)}
                    {entry.actor_name || entry.actor ? (
                      <> · <span className="font-sans font-medium text-gray-700">{entry.actor_name || entry.actor}</span></>
                    ) : null}
                  </div>
                  <div className="text-sm text-gray-800 font-medium">
                    {entry.action || 'ttr.event'}
                    {entry.from || entry.to ? (
                      <span className="text-xs text-gray-500 font-normal ml-2">
                        {entry.from ? `${entry.from} → ` : ''}{entry.to || ''}
                      </span>
                    ) : null}
                  </div>
                  {entry.details && (
                    <div className="text-sm text-gray-600 whitespace-pre-wrap mt-0.5">{entry.details}</div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No activity recorded yet.</p>
          )}
        </CardContent>
      </Card>

      <MarkNotRequiredModal
        open={notRequiredOpen}
        onClose={() => !markingNotRequired && setNotRequiredOpen(false)}
        onSubmit={handleMarkNotRequired}
        submitting={markingNotRequired}
      />

      <FileTtrModal
        open={filingOpen}
        onClose={() => !filing && setFilingOpen(false)}
        onSubmit={handleFile}
        submitting={filing}
      />
      <ScreenHelpPanel
        screenKey={TTR_DETAIL_HELP_SCREEN_KEY}
        open={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
        sectionIdPrefix="ttr-detail-help"
      />
    </div>
  )
}
